import React, { useState, useCallback, createContext, useContext } from 'react';

/**
 * Required props for a component to be rendered as a modal.
 */
export type ModalComponentProps<TResult> = {
  onConfirm: (result: TResult) => void;
  onCancel: () => void;
};

type ModalConfig<TProps, TResult> = {
  component: React.ComponentType<TProps & ModalComponentProps<TResult>>;
  props: Omit<TProps, keyof ModalComponentProps<TResult>>;
};

type ModalContextType = {
  showModal: <TProps, TResult>(config: ModalConfig<TProps, TResult>) => Promise<TResult>;
};

const ModalContext = createContext<ModalContextType | null>(null);

/**
 * Access the modal context.
 * Must be called from within a ModalProvider.
 * @returns Current modal context.
 */
export function useModal() {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error('useModal must be used within a ModalProvider');
  }
  return context;
}

/**
 * Provider component to create a modal context.
 */
export function ModalProvider({ children }: { children: React.ReactNode }) {
  const [activeModal, setActiveModal] = useState<{
    config: ModalConfig<any, any>;
    resolve: (value: any) => void;
    reject: () => void;
  } | null>(null);

  const showModal = useCallback(<TProps, TResult>(
    config: ModalConfig<TProps, TResult>
  ): Promise<TResult> => {
    return new Promise<TResult>((resolve, reject) => {
      setActiveModal({ config, resolve, reject });
    });
  }, []);

  const handleConfirm = (result: any) => {
    activeModal?.resolve(result);
    setActiveModal(null);
  };

  const handleCancel = () => {
    activeModal?.resolve(undefined);
    setActiveModal(null);
  };

  // Render the active modal
  const modalElement = activeModal ? (
    <activeModal.config.component
      {...activeModal.config.props}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
    />
  ) : null;

  return (
    <ModalContext.Provider value={{ showModal }}>
      {children}
      {modalElement}
    </ModalContext.Provider>
  );
}
